// Admin Dashboard — visible only when user.role === "admin".
// Three tabs: Overview (stats), Users (table + role/delete), Projects (cross-user list + delete).

import React, { useEffect, useState, useCallback } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Input } from "../components/ui/input";
import { Checkbox } from "../components/ui/checkbox";
import {
  Users,
  FolderOpen,
  Activity,
  ShieldCheck,
  AlertTriangle,
  Trash2,
  ArrowLeft,
  Loader2,
  ExternalLink,
} from "lucide-react";
import { useAuth, formatApiError } from "../lib/auth";
import { toast } from "sonner";
import AdminModelAssetsTab from "./AdminModelAssetsTab";
import AdminEmailTab from "./AdminEmailTab";
import AdminPerformanceTab from "./AdminPerformanceTab";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const StatCard = ({ icon: Icon, label, value, intent = "default" }) => (
  <Card data-testid={`stat-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
    <CardContent className="pt-5 pb-4 px-5 flex items-center gap-3">
      <div
        className={`h-10 w-10 rounded-lg flex items-center justify-center ${
          intent === "warn"
            ? "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300"
            : "bg-muted text-muted-foreground"
        }`}
      >
        <Icon className="h-5 w-5" />
      </div>
      <div>
        <div className="text-2xl font-bold leading-none">{value}</div>
        <div className="text-xs text-muted-foreground mt-1 uppercase tracking-wide">{label}</div>
      </div>
    </CardContent>
  </Card>
);

export default function AdminDashboard() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [projectsLoading, setProjectsLoading] = useState(false);
  const [statsLoading, setStatsLoading] = useState(false);
  const [filter, setFilter] = useState("");
  const [projFilter, setProjFilter] = useState("");
  const [busyId, setBusyId] = useState(null);
  // Bulk selection per table (separate Sets so users + projects don't collide).
  const [selUsers, setSelUsers] = useState(() => new Set());
  const [selProjects, setSelProjects] = useState(() => new Set());
  const [bulkBusy, setBulkBusy] = useState(false);

  const refreshStats = useCallback(async () => {
    setStatsLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/stats`);
      setStats(data);
    } catch (err) {
      toast.error("Stats: " + formatApiError(err));
    } finally {
      setStatsLoading(false);
    }
  }, []);

  const refreshUsers = useCallback(async () => {
    setUsersLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/users`);
      setUsers(data);
    } catch (err) {
      toast.error("Users: " + formatApiError(err));
    } finally {
      setUsersLoading(false);
    }
  }, []);

  const refreshProjects = useCallback(async () => {
    setProjectsLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/projects`);
      setProjects(data);
    } catch (err) {
      toast.error("Projects: " + formatApiError(err));
    } finally {
      setProjectsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!user || user.role !== "admin") return;
    refreshStats();
    refreshUsers();
    refreshProjects();
  }, [user, refreshStats, refreshUsers, refreshProjects]);

  if (!loading && !user) return <Navigate to="/login" replace />;
  if (!loading && user && user.role !== "admin") {
    return <Navigate to="/home" replace />;
  }

  const setRole = async (id, role) => {
    setBusyId(id);
    try {
      await axios.patch(`${API}/admin/users/${id}/role`, { role });
      toast.success(`Role updated to ${role}`);
      refreshUsers();
      refreshStats();
    } catch (err) {
      toast.error("Role change failed: " + formatApiError(err));
    } finally {
      setBusyId(null);
    }
  };

  const deleteUser = async (id, email) => {
    // eslint-disable-next-line no-restricted-globals
    if (!window.confirm(`Delete user "${email}" and ALL their projects? This can't be undone.`))
      return;
    setBusyId(id);
    try {
      await axios.delete(`${API}/admin/users/${id}`);
      toast.success("User deleted");
      refreshUsers();
      refreshProjects();
      refreshStats();
    } catch (err) {
      toast.error("Delete failed: " + formatApiError(err));
    } finally {
      setBusyId(null);
    }
  };

  const deleteProject = async (id, name) => {
    // eslint-disable-next-line no-restricted-globals
    if (!window.confirm(`Delete project "${name}"? This can't be undone.`)) return;
    setBusyId(id);
    try {
      await axios.delete(`${API}/admin/projects/${id}`);
      toast.success("Project deleted");
      refreshProjects();
      refreshStats();
    } catch (err) {
      toast.error("Delete failed: " + formatApiError(err));
    } finally {
      setBusyId(null);
    }
  };

  // Bulk delete users — skips the currently logged-in admin (can't delete self).
  // Runs N DELETE requests in parallel; aggregates failures into one toast.
  const bulkDeleteUsers = async () => {
    const ids = [...selUsers].filter((id) => id !== user.id);
    if (ids.length === 0) {
      toast.error("Nothing to delete (you can't delete yourself)");
      return;
    }
    // eslint-disable-next-line no-restricted-globals
    if (!window.confirm(`Delete ${ids.length} user${ids.length === 1 ? "" : "s"} and ALL their projects? This can't be undone.`)) return;
    setBulkBusy(true);
    const failures = [];
    await Promise.all(ids.map(async (id) => {
      try { await axios.delete(`${API}/admin/users/${id}`); }
      catch (e) { failures.push({ id, error: formatApiError(e) }); }
    }));
    setBulkBusy(false);
    setSelUsers(new Set());
    if (failures.length === 0) {
      toast.success(`Deleted ${ids.length} user(s)`);
    } else {
      toast.error(`${failures.length}/${ids.length} delete(s) failed — see console`);
      // eslint-disable-next-line no-console
      console.warn("Bulk user delete failures:", failures);
    }
    refreshUsers();
    refreshProjects();
    refreshStats();
  };

  // Bulk delete projects across users.
  const bulkDeleteProjects = async () => {
    const ids = [...selProjects];
    if (ids.length === 0) return;
    // eslint-disable-next-line no-restricted-globals
    if (!window.confirm(`Delete ${ids.length} project${ids.length === 1 ? "" : "s"}? This can't be undone.`)) return;
    setBulkBusy(true);
    const failures = [];
    await Promise.all(ids.map(async (id) => {
      try { await axios.delete(`${API}/admin/projects/${id}`); }
      catch (e) { failures.push({ id, error: formatApiError(e) }); }
    }));
    setBulkBusy(false);
    setSelProjects(new Set());
    if (failures.length === 0) {
      toast.success(`Deleted ${ids.length} project(s)`);
    } else {
      toast.error(`${failures.length}/${ids.length} delete(s) failed — see console`);
    }
    refreshProjects();
    refreshStats();
  };

  const openProjectAsAdmin = (id) => {
    // Re-uses the wizard's URL-driven hydration (admin reads via /api/admin/projects/{id}
    // is identical to /api/projects/{id} response shape, but the wizard calls the
    // user endpoint — admin can only OPEN their OWN projects through /wizard.
    // Keep this as a hint; for cross-user view, expose JSON in a future iteration).
    navigate(`/wizard?projectId=${id}`);
  };

  const filteredUsers = users.filter(
    (u) =>
      !filter ||
      u.email.toLowerCase().includes(filter.toLowerCase()) ||
      (u.name || "").toLowerCase().includes(filter.toLowerCase())
  );

  const filteredProjects = projects.filter(
    (p) =>
      !projFilter ||
      p.name.toLowerCase().includes(projFilter.toLowerCase()) ||
      p.owner_email.toLowerCase().includes(projFilter.toLowerCase())
  );

  return (
    <div className="container mx-auto py-8 px-4 xl:px-10 max-w-[1728px]" data-testid="admin-dashboard">
      <div className="flex items-center justify-between mb-6">
        <div>
          <Link
            to="/home"
            className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center mb-1"
            data-testid="admin-back-link"
          >
            <ArrowLeft className="h-3 w-3 mr-1" /> My Projects
          </Link>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <ShieldCheck className="h-6 w-6 text-muted-foreground" /> Admin Dashboard
          </h1>
          <p className="text-sm text-muted-foreground">ZAMBA Manufacturer Console</p>
        </div>
      </div>

      <Tabs defaultValue="overview" className="space-y-4">
        <TabsList>
          <TabsTrigger value="overview" data-testid="admin-tab-overview">
            Overview
          </TabsTrigger>
          <TabsTrigger value="users" data-testid="admin-tab-users">
            Users
          </TabsTrigger>
          <TabsTrigger value="projects" data-testid="admin-tab-projects">
            Projects
          </TabsTrigger>
          <TabsTrigger value="documents" data-testid="admin-tab-documents">
            Documents
          </TabsTrigger>
          <TabsTrigger value="performance" data-testid="admin-tab-performance">
            Performance Data
          </TabsTrigger>
          <TabsTrigger value="email" data-testid="admin-tab-email">
            Email
          </TabsTrigger>
        </TabsList>

        {/* Overview */}
        <TabsContent value="overview" className="space-y-4">
          {statsLoading || !stats ? (
            <div className="py-10 flex justify-center">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground/70" />
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              <StatCard icon={Users} label="Total Users" value={stats.total_users} />
              <StatCard icon={ShieldCheck} label="Admins" value={stats.admin_users} />
              <StatCard icon={FolderOpen} label="Total Projects" value={stats.total_projects} />
              <StatCard icon={Activity} label="Active 7d" value={stats.recent_projects_7d} />
              <StatCard
                icon={AlertTriangle}
                label="Lockouts"
                value={stats.active_lockouts}
                intent={stats.active_lockouts > 0 ? "warn" : "default"}
              />
            </div>
          )}
        </TabsContent>

        {/* Users */}
        <TabsContent value="users">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2">
                <Users className="h-4 w-4" /> Users ({filteredUsers.length})
              </CardTitle>
              <Input
                placeholder="Filter by email or name…"
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                className="w-64 h-8 text-sm"
                data-testid="admin-users-filter"
              />
            </CardHeader>
            <CardContent>
              {/* Bulk-action toolbar — appears once 1+ users are selected. */}
              {selUsers.size > 0 && (
                <div
                  className="flex items-center gap-3 mb-3 p-2 px-3 bg-primary text-primary-foreground rounded-md text-sm"
                  data-testid="admin-users-bulk-toolbar"
                >
                  <span className="font-medium">
                    {selUsers.size} user{selUsers.size === 1 ? "" : "s"} selected
                  </span>
                  <div className="flex-1" />
                  <Button
                    size="sm"
                    variant="ghost"
                    className="text-primary-foreground hover:bg-primary/80"
                    onClick={() => setSelUsers(new Set())}
                  >
                    Clear
                  </Button>
                  <Button
                    size="sm"
                    className="bg-rose-600 hover:bg-rose-700 text-white"
                    disabled={bulkBusy}
                    onClick={bulkDeleteUsers}
                    data-testid="admin-users-bulk-delete"
                  >
                    {bulkBusy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Trash2 className="h-4 w-4 mr-1" />}
                    Delete Selected
                  </Button>
                </div>
              )}
              {usersLoading ? (
                <div className="py-10 flex justify-center">
                  <Loader2 className="h-5 w-5 animate-spin text-muted-foreground/70" />
                </div>
              ) : (
                <div className="overflow-x-auto">
                <Table className="text-sm min-w-full">
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-10">
                        <Checkbox
                          checked={
                            filteredUsers.filter((u) => u.id !== user.id).length > 0 &&
                            filteredUsers.filter((u) => u.id !== user.id).every((u) => selUsers.has(u.id))
                              ? true
                              : selUsers.size > 0 ? "indeterminate" : false
                          }
                          onCheckedChange={() => {
                            const eligible = filteredUsers.filter((u) => u.id !== user.id).map((u) => u.id);
                            const allOn = eligible.every((id) => selUsers.has(id));
                            setSelUsers(allOn ? new Set() : new Set(eligible));
                          }}
                          data-testid="admin-users-select-all"
                          aria-label="Select all users"
                        />
                      </TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Name</TableHead>
                      <TableHead>Role</TableHead>
                      <TableHead className="text-right">Projects</TableHead>
                      <TableHead>Last Activity</TableHead>
                      <TableHead className="w-44 text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredUsers.map((u) => (
                      <TableRow
                        key={u.id}
                        data-testid={`admin-user-row-${u.id}`}
                        className={selUsers.has(u.id) ? "bg-muted" : undefined}
                      >
                        <TableCell>
                          {u.id !== user.id ? (
                            <Checkbox
                              checked={selUsers.has(u.id)}
                              onCheckedChange={() => {
                                setSelUsers((prev) => {
                                  const next = new Set(prev);
                                  if (next.has(u.id)) next.delete(u.id); else next.add(u.id);
                                  return next;
                                });
                              }}
                              data-testid={`admin-user-select-${u.id}`}
                            />
                          ) : (
                            <span className="text-[10px] text-muted-foreground/70">—</span>
                          )}
                        </TableCell>
                        <TableCell className="font-mono text-xs">{u.email}</TableCell>
                        <TableCell>{u.name || "—"}</TableCell>
                        <TableCell>
                          <span
                            className={`inline-block px-2 py-0.5 rounded text-[11px] font-medium ${
                              u.role === "admin"
                                ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300"
                                : "bg-muted text-muted-foreground"
                            }`}
                          >
                            {u.role}
                          </span>
                        </TableCell>
                        <TableCell className="text-right font-mono">{u.project_count}</TableCell>
                        <TableCell className="text-xs text-muted-foreground">
                          {u.last_project_update
                            ? new Date(u.last_project_update).toLocaleString()
                            : "—"}
                        </TableCell>
                        <TableCell className="text-right space-x-1">
                          {u.id !== user.id && (
                            <>
                              <Button
                                size="sm"
                                variant="outline"
                                disabled={busyId === u.id}
                                onClick={() =>
                                  setRole(u.id, u.role === "admin" ? "user" : "admin")
                                }
                                data-testid={`role-toggle-${u.id}`}
                              >
                                {u.role === "admin" ? "Demote" : "Promote"}
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                disabled={busyId === u.id}
                                onClick={() => deleteUser(u.id, u.email)}
                                data-testid={`delete-user-${u.id}`}
                              >
                                <Trash2 className="h-4 w-4 text-rose-600" />
                              </Button>
                            </>
                          )}
                          {u.id === user.id && (
                            <span className="text-[11px] text-muted-foreground/70">you</span>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Projects */}
        <TabsContent value="projects">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2">
                <FolderOpen className="h-4 w-4" /> Projects ({filteredProjects.length})
              </CardTitle>
              <Input
                placeholder="Filter by project name or owner…"
                value={projFilter}
                onChange={(e) => setProjFilter(e.target.value)}
                className="w-64 h-8 text-sm"
                data-testid="admin-projects-filter"
              />
            </CardHeader>
            <CardContent>
              {selProjects.size > 0 && (
                <div
                  className="flex items-center gap-3 mb-3 p-2 px-3 bg-primary text-primary-foreground rounded-md text-sm"
                  data-testid="admin-projects-bulk-toolbar"
                >
                  <span className="font-medium">
                    {selProjects.size} project{selProjects.size === 1 ? "" : "s"} selected
                  </span>
                  <div className="flex-1" />
                  <Button
                    size="sm"
                    variant="ghost"
                    className="text-primary-foreground hover:bg-primary/80"
                    onClick={() => setSelProjects(new Set())}
                  >
                    Clear
                  </Button>
                  <Button
                    size="sm"
                    className="bg-rose-600 hover:bg-rose-700 text-white"
                    disabled={bulkBusy}
                    onClick={bulkDeleteProjects}
                    data-testid="admin-projects-bulk-delete"
                  >
                    {bulkBusy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Trash2 className="h-4 w-4 mr-1" />}
                    Delete Selected
                  </Button>
                </div>
              )}
              {projectsLoading ? (
                <div className="py-10 flex justify-center">
                  <Loader2 className="h-5 w-5 animate-spin text-muted-foreground/70" />
                </div>
              ) : filteredProjects.length === 0 ? (
                <div className="py-10 text-center text-sm text-muted-foreground" data-testid="admin-projects-empty">
                  No projects.
                </div>
              ) : (
                <div className="overflow-x-auto">
                <Table className="text-sm min-w-full">
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-10">
                        <Checkbox
                          checked={
                            filteredProjects.length > 0 &&
                            filteredProjects.every((p) => selProjects.has(p.id))
                              ? true
                              : selProjects.size > 0 ? "indeterminate" : false
                          }
                          onCheckedChange={() => {
                            const ids = filteredProjects.map((p) => p.id);
                            const allOn = ids.every((id) => selProjects.has(id));
                            setSelProjects(allOn ? new Set() : new Set(ids));
                          }}
                          data-testid="admin-projects-select-all"
                          aria-label="Select all projects"
                        />
                      </TableHead>
                      <TableHead>Name</TableHead>
                      <TableHead>Owner</TableHead>
                      <TableHead>Updated</TableHead>
                      <TableHead>Created</TableHead>
                      <TableHead className="w-32 text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredProjects.map((p) => (
                      <TableRow
                        key={p.id}
                        data-testid={`admin-project-row-${p.id}`}
                        className={selProjects.has(p.id) ? "bg-muted" : undefined}
                      >
                        <TableCell>
                          <Checkbox
                            checked={selProjects.has(p.id)}
                            onCheckedChange={() => {
                              setSelProjects((prev) => {
                                const next = new Set(prev);
                                if (next.has(p.id)) next.delete(p.id); else next.add(p.id);
                                return next;
                              });
                            }}
                            data-testid={`admin-project-select-${p.id}`}
                          />
                        </TableCell>
                        <TableCell className="font-medium">{p.name}</TableCell>
                        <TableCell className="font-mono text-xs">{p.owner_email}</TableCell>
                        <TableCell className="text-xs text-muted-foreground">
                          {new Date(p.updated_at).toLocaleString()}
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground">
                          {new Date(p.created_at).toLocaleDateString()}
                        </TableCell>
                        <TableCell className="text-right space-x-1">
                          {p.owner_id === user.id && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => openProjectAsAdmin(p.id)}
                              data-testid={`admin-open-${p.id}`}
                              title="Open in wizard (your projects only)"
                            >
                              <ExternalLink className="h-3.5 w-3.5" />
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="ghost"
                            disabled={busyId === p.id}
                            onClick={() => deleteProject(p.id, p.name)}
                            data-testid={`admin-delete-project-${p.id}`}
                          >
                            <Trash2 className="h-4 w-4 text-rose-600" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Documents — admin uploader for per-model reference PDFs */}
        <TabsContent value="documents">
          <AdminModelAssetsTab />
        </TabsContent>
        {/* Performance Data — Coil + Airflow Excel/CSV import + export */}
        <TabsContent value="performance">
          <AdminPerformanceTab />
        </TabsContent>
        {/* Email — SMTP config, connection test, send-test, signup notes */}
        <TabsContent value="email">
          <AdminEmailTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}
